/**
 * DeliveryLocationCard.jsx — iter-298f
 * ═══════════════════════════════════════════════════════════════════
 * Vendor / Admin-facing customer delivery-location editor for an order.
 * Renders an interactive Leaflet + OpenStreetMap map inline in the
 * Order Detail modal and exposes THREE actions to fix wrong customer
 * coordinates before Ship Now:
 *
 *   1. 🔄  Re-geocode from address     → backend Ola Maps call (OSM fallback), moves pin
 *   2. ✏️  Adjust pin                  → drag/click on the map, "Save pin"
 *   3. 📍 Use current location         → browser geolocation
 *
 * Reads / writes go through the Coordinate SSOT (`services/location/`),
 * so Shiprocket Quick, Delhivery Hyperlocal, Porter, Borzo etc. all
 * consume the updated coord automatically — no per-provider re-plumbing
 * needed.
 *
 * Leaflet is CDN-loaded lazily (same technique as the storefront
 * `_monolith.js.jinja` delivery-pin-picker) so we don't add a heavy npm
 * dep to package.json for one component.
 *
 * ═══════════════════════════════════════════════════════════════════
 * Test IDs (every interactive element carries one — required per repo
 * convention so QA can drive precise flows):
 *
 *   delivery-location-card
 *   delivery-location-source-badge
 *   delivery-location-confidence-badge
 *   delivery-location-distance-km
 *   delivery-location-map
 *   delivery-location-btn-regeocode
 *   delivery-location-btn-adjust
 *   delivery-location-btn-current
 *   delivery-location-btn-save-pin
 *   delivery-location-btn-reset
 *   delivery-location-history-toggle
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, RefreshCw, Locate, Save, RotateCcw, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Coord source → colour / label ─────────────────────────────────
const SOURCE_STYLE = {
  customer_confirmed_osm: { colour: 'emerald', label: 'Customer confirmed', hint: 'Buyer dropped a pin themselves.' },
  vendor_confirmed_osm:   { colour: 'sky',     label: 'Vendor confirmed',   hint: 'You confirmed this pin manually.' },
  backend_ola_geocode:    { colour: 'amber',   label: 'Address geocoded (Ola)', hint: 'Resolved via Ola Maps from the order address — high precision for India.' },
  backend_osm_geocode:    { colour: 'amber',   label: 'Address geocoded (OSM)', hint: 'Resolved via OpenStreetMap from the order address — used only when Ola Maps was unavailable.' },
  shopify_geocode:        { colour: 'amber',   label: 'Shopify geocode',    hint: "Shopify's own geocoder — usually ~1 km precision." },
  pincode_centroid:       { colour: 'rose',    label: 'Pincode centroid',   hint: 'Coarse centroid of the pincode — ~2 km precision. Consider re-geocoding.' },
  city_centroid_last_resort: { colour: 'rose', label: 'City centroid',      hint: 'Last-resort city fallback — usually inaccurate.' },
};

const CONFIDENCE_STYLE = {
  high:   { colour: 'emerald', label: 'High' },
  medium: { colour: 'amber',   label: 'Medium' },
  low:    { colour: 'rose',    label: 'Low' },
};

// ─── Lazy CDN loader for Leaflet ───────────────────────────────────
let _leafletPromise = null;
function loadLeaflet() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.L) return Promise.resolve(window.L);
  if (_leafletPromise) return _leafletPromise;

  _leafletPromise = new Promise((resolve, reject) => {
    if (!document.getElementById('mp-leaflet-css')) {
      const css = document.createElement('link');
      css.id = 'mp-leaflet-css';
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(css);
    }
    if (window.L) return resolve(window.L);
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    s.async = true;
    s.onload = () => (window.L ? resolve(window.L) : reject(new Error('Leaflet failed to initialise')));
    s.onerror = () => reject(new Error('Leaflet CDN failed to load'));
    document.body.appendChild(s);
  });
  return _leafletPromise;
}

// ─── Component ─────────────────────────────────────────────────────
export default function DeliveryLocationCard({ order, onCoordChanged }) {
  const orderId = order?.id;
  const authHeader = useCallback(() => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');       // '' | 'regeocode' | 'save' | 'current'
  const [expanded, setExpanded] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Draft pin (before user hits "Save pin").
  const [draftPin, setDraftPin] = useState(null); // { lat, lng }

  // Leaflet handles.
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const vendorMarkerRef = useRef(null);

  // ─── Fetch resolved envelope ─────────────────────────────────────
  const fetchResolved = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/orders/${orderId}/customer-location/resolved`, {
        headers: authHeader(),
      });
      const j = await r.json();
      setResolved(j);
    } catch (e) {
      console.error('[DeliveryLocationCard] resolve failed', e);
      toast.error('Could not load customer coordinate');
    } finally {
      setLoading(false);
    }
  }, [orderId, authHeader]);

  useEffect(() => { fetchResolved(); }, [fetchResolved]);

  // ─── Init the Leaflet map when expanded ──────────────────────────
  useEffect(() => {
    if (!expanded || !resolved?.customer?.lat) return;
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapDivRef.current) return;
        if (mapRef.current) return; // already initialised

        const cLat = resolved.customer.lat;
        const cLng = resolved.customer.lng;

        const map = L.map(mapDivRef.current, {
          center: [cLat, cLng],
          zoom: 15,
          attributionControl: true,
          scrollWheelZoom: false,
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap contributors',
        }).addTo(map);

        // Customer marker — draggable
        const cust = L.marker([cLat, cLng], {
          draggable: true,
          title: 'Customer delivery location',
        }).addTo(map);
        cust.on('dragend', () => {
          const p = cust.getLatLng();
          setDraftPin({ lat: p.lat, lng: p.lng });
        });
        markerRef.current = cust;

        // Click-to-reposition
        map.on('click', (e) => {
          cust.setLatLng(e.latlng);
          setDraftPin({ lat: e.latlng.lat, lng: e.latlng.lng });
        });

        // Vendor pickup marker (non-draggable, for context)
        if (resolved.vendor?.lat && resolved.vendor?.lng) {
          const vendorIcon = L.divIcon({
            html: '<div style="background:#3b82f6;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 0 4px rgba(0,0,0,.4)"></div>',
            className: '', iconSize: [14, 14], iconAnchor: [7, 7],
          });
          vendorMarkerRef.current = L.marker([resolved.vendor.lat, resolved.vendor.lng], {
            icon: vendorIcon,
            title: 'Your pickup location',
          }).addTo(map);
        }

        mapRef.current = map;
        setTimeout(() => map.invalidateSize(), 100);
      })
      .catch((e) => {
        console.error('[DeliveryLocationCard] Leaflet load failed', e);
        toast.error('Map failed to load. You can still re-geocode from address.');
      });

    return () => { cancelled = true; };
  }, [expanded, resolved]);

  // Move marker when resolved changes (e.g. after re-geocode success)
  useEffect(() => {
    if (!mapRef.current || !markerRef.current || !resolved?.customer?.lat) return;
    const { lat, lng } = resolved.customer;
    markerRef.current.setLatLng([lat, lng]);
    mapRef.current.panTo([lat, lng]);
    setDraftPin(null);
  }, [resolved]);

  // ─── Cleanup ─────────────────────────────────────────────────────
  useEffect(() => () => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, []);

  // ─── Actions ─────────────────────────────────────────────────────
  const regeocodeFromAddress = async () => {
    setBusy('regeocode');
    try {
      const r = await fetch(`${API}/api/orders/${orderId}/customer-location/re-geocode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
      });
      const j = await r.json();
      if (r.status === 409) {
        toast.warning('Customer already confirmed this location. Use "Adjust pin" with a reason if you must override.');
      } else if (!j.ok) {
        toast.error(j.reason || j.error || 'Re-geocode failed');
      } else {
        const viaLabel = j.customer_location.source === 'backend_ola_geocode' ? 'Ola Maps' : 'OpenStreetMap';
        toast.success(`Re-geocoded via ${viaLabel} → ${j.customer_location.latitude.toFixed(5)}, ${j.customer_location.longitude.toFixed(5)}`);
        await fetchResolved();
        onCoordChanged?.();
      }
    } catch (e) {
      toast.error(`Re-geocode failed: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return toast.error('Browser has no geolocation API');
    setBusy('current');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDraftPin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        if (markerRef.current) markerRef.current.setLatLng([pos.coords.latitude, pos.coords.longitude]);
        if (mapRef.current) mapRef.current.panTo([pos.coords.latitude, pos.coords.longitude]);
        setBusy('');
        toast.info(`Pin moved to your current location — click "Save pin" to persist.`);
      },
      (err) => { toast.error(`Geolocation blocked: ${err.message}`); setBusy(''); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const saveManualPin = async () => {
    if (!draftPin) return toast.error('Drag or click the map first to set a pin');
    setBusy('save');
    try {
      const r = await fetch(`${API}/api/orders/${orderId}/customer-location/set-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          latitude: draftPin.lat,
          longitude: draftPin.lng,
          reason: 'vendor_manual_pin_via_order_detail',
        }),
      });
      const j = await r.json();
      if (!j.ok) {
        toast.error(j.detail || j.error || 'Save failed');
      } else {
        toast.success('Pin saved — Shiprocket Quick will now use this coord.');
        setDraftPin(null);
        await fetchResolved();
        onCoordChanged?.();
      }
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  const resetDraft = () => {
    if (!markerRef.current || !resolved?.customer?.lat) return;
    markerRef.current.setLatLng([resolved.customer.lat, resolved.customer.lng]);
    if (mapRef.current) mapRef.current.panTo([resolved.customer.lat, resolved.customer.lng]);
    setDraftPin(null);
  };

  // ─── Render ──────────────────────────────────────────────────────
  if (!orderId) return null;

  const cust = resolved?.customer;
  const vend = resolved?.vendor;
  const distanceKm = resolved?.distance_km;
  const srcStyle = SOURCE_STYLE[cust?.source] || { colour: 'gray', label: cust?.source || 'unknown', hint: '' };
  const confStyle = CONFIDENCE_STYLE[cust?.confidence] || { colour: 'gray', label: cust?.confidence || '?' };

  const history = order?.customer_location?.history || [];

  return (
    <div
      data-testid="delivery-location-card"
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className={`p-2 rounded-lg bg-${srcStyle.colour}-50`}>
            <MapPin className={`w-5 h-5 text-${srcStyle.colour}-600`} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Delivery Location</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                data-testid="delivery-location-source-badge"
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-${srcStyle.colour}-100 text-${srcStyle.colour}-800`}
              >
                {srcStyle.label}
              </span>
              <span
                data-testid="delivery-location-confidence-badge"
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-${confStyle.colour}-100 text-${confStyle.colour}-800`}
              >
                {confStyle.label} confidence
              </span>
              {distanceKm != null && (
                <span
                  data-testid="delivery-location-distance-km"
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700"
                >
                  {distanceKm.toFixed(2)} km to pickup
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">{srcStyle.hint}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="delivery-location-toggle-map"
        >
          {expanded ? <>Hide map <ChevronUp className="w-3 h-3" /></> : <>Show map <ChevronDown className="w-3 h-3" /></>}
        </button>
      </div>

      {/* Warning banner for coarse sources */}
      {(cust?.source === 'pincode_centroid' || cust?.source === 'city_centroid_last_resort' || cust?.confidence === 'low') && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            The customer coordinate is a coarse fallback (~2 km precision). Shiprocket Quick pricing and rider ETA
            will be inaccurate. Click <b>Re-geocode from address</b> or drop a pin below to fix.
          </span>
        </div>
      )}

      {/* Map */}
      {expanded && (
        <div className="mt-3">
          <div
            ref={mapDivRef}
            data-testid="delivery-location-map"
            className="w-full rounded-lg border border-slate-200 bg-slate-50"
            style={{ height: 260 }}
          />
          {draftPin && (
            <div className="mt-2 text-xs text-slate-600 flex items-center justify-between">
              <span>
                Pin at <b>{draftPin.lat.toFixed(5)}, {draftPin.lng.toFixed(5)}</b> — unsaved.
              </span>
              <button
                type="button"
                onClick={resetDraft}
                data-testid="delivery-location-btn-reset"
                className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900"
              >
                <RotateCcw className="w-3 h-3" /> reset
              </button>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={regeocodeFromAddress}
          disabled={busy === 'regeocode' || loading}
          data-testid="delivery-location-btn-regeocode"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy === 'regeocode' ? 'animate-spin' : ''}`} />
          Re-geocode from address
        </button>
        {expanded && (
          <>
            <button
              type="button"
              onClick={useCurrentLocation}
              disabled={busy === 'current'}
              data-testid="delivery-location-btn-current"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              <Locate className={`w-3.5 h-3.5 ${busy === 'current' ? 'animate-spin' : ''}`} />
              Use current location
            </button>
            <button
              type="button"
              onClick={saveManualPin}
              disabled={!draftPin || busy === 'save'}
              data-testid="delivery-location-btn-save-pin"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className={`w-3.5 h-3.5 ${busy === 'save' ? 'animate-pulse' : ''}`} />
              Save pin
            </button>
          </>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={() => setHistoryOpen((h) => !h)}
            className="text-[11px] font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="delivery-location-history-toggle"
          >
            {historyOpen ? 'Hide' : 'Show'} coordinate history ({history.length})
            {historyOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {historyOpen && (
            <ul className="mt-2 space-y-1 text-[11px] text-slate-600">
              {history.slice().reverse().map((h, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-slate-400 whitespace-nowrap">{h.at?.slice(0, 16).replace('T', ' ')}</span>
                  <span>
                    <b className="text-slate-800">{h.by_role || 'system'}</b> · {h.action}
                    {h.reason ? ` (${h.reason})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
